import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { createTranslation, theme } from '@app/helpers';
import { Check, Close, Remove } from '@material-ui/icons';
import { Typography } from '@material-ui/core';
import { IVaultPasswordPolicyRequirements } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import { PasslyTooltip } from '@app/componentsV2';

const useStyles = makeStyles({
    table: {
        maxWidth: 950,
    },
    cell: {
        padding: '2px 2px 2px 16px'
    },
    cellHeader: {
        paddingLeft: 16
    }
});

interface IProps {
    passwordPolicies: IVaultPasswordPolicyRequirements[];
}

export default function PasswordPolicyRequirements(props: IProps) {
    const classes = useStyles();
    const { translation } = useTypedTranslation();

    const getCheck = () => {
        return (
            <PasslyTooltip title={translation('REQUIRED_ONLY')}>
                <Check style={{ color: theme.palette.success.main }} />
            </PasslyTooltip>
        );
    };

    const getX = () => {
        return (
            <PasslyTooltip title={translation('NOT_ALLOWED')}>
                <Close style={{ color: theme.palette.error.main }} />
            </PasslyTooltip>
        );
    };

    const getDash = () => {
        return (
            <PasslyTooltip title={translation('NOT_REQUIRED')}>
                <Remove />
            </PasslyTooltip>
        );
    };

    const handleSpecials = (row: IVaultPasswordPolicyRequirements) => {
        if (row.RequireSpecials !== undefined) {
            // this happens on a real policy assigned to the vault
            if (row.RequireSpecials) {
                return getCheck();
            }
            else {
                return getX();
            }
        }
        else {
            // this case happens on seeded complexity policies
            return row.OnlyNumbers ? getX() : row.SpecialCharactersUsed !== '' ? row.SpecialCharactersUsed : getDash();
        }
    }

    return (
        <Table className={classes.table} size="small">
            <TableHead>
                <TableRow>
                    <TableCell className={classes.cellHeader}>
                        <Typography variant='subtitle1'>
                            {translation('POLICY_NAME')}
                        </Typography>
                    </TableCell>

                    <TableCell className={classes.cellHeader} align="left">
                        <Typography variant='subtitle1'>
                            {translation('LENGTH')}
                        </Typography>
                    </TableCell>

                    <TableCell className={classes.cellHeader} align="left">
                        <Typography variant='subtitle1'>
                            {translation('DIGITS')}
                        </Typography>
                    </TableCell>

                    <TableCell className={classes.cellHeader} align="left">
                        <Typography variant='subtitle1'>
                            {translation('UPPERCASE')}
                        </Typography>
                    </TableCell>

                    <TableCell className={classes.cellHeader} align="left">
                        <Typography variant='subtitle1'>
                            {translation('LOWERCASE')}
                        </Typography>
                    </TableCell>

                    <TableCell className={classes.cellHeader} align="left">
                        <Typography variant='subtitle1'>
                            {translation('SPECIAL_CHARACTERS')}
                        </Typography>
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {props.passwordPolicies.map((row, index) => (
                    <TableRow key={row.value} style={index % 2 ? { background: theme.palette.common.white } : { background: theme.tablerow.secondary }}>
                        <TableCell component="th" scope="row" className={classes.cell}>
                            <Typography variant='subtitle2'>
                                {index === 0 ? row.label : translation(createTranslation(row.label))}
                            </Typography>
                        </TableCell>

                        <TableCell className={classes.cell} align="left">
                            {row.MinimumLength}-{row.MaximumLength}
                        </TableCell>

                        <TableCell className={classes.cell} align="left">
                            {row.RequireDigits ? getCheck() : getDash()}
                        </TableCell>

                        <TableCell className={classes.cell} align="left">
                            {row.OnlyNumbers ? getX() : row.RequireLowercase ? getCheck() : getDash()}
                        </TableCell>

                        <TableCell className={classes.cell} align="left">
                            {row.OnlyNumbers ? getX() : row.RequireUppercase ? getCheck() : getDash()}
                        </TableCell>

                        <TableCell className=
                            {classes.cell} align="left">{handleSpecials(row)}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>

    );
}